import { createElement, type ReactNode } from "react";
import { Euro, Hourglass } from "lucide-react";
import { at } from "../../utils/analyticsI18n";
import { formatAnalyticsCurrency } from "../shared/analyticsFormatting";
import type { MetricCardTrend } from "../shared/MetricCard";
import { METRIC_CARD_ICON_COLOR } from "../shared/constants";

const DELTA_SUFFIX = () =>
  at("common.fromPreviousPeriod", " from previous period");

export type ServicesRapidRevenueMetric = {
  title: string;
  value: string;
  delta?: string;
  description?: string;
  trend?: MetricCardTrend;
  icon: ReactNode;
  iconColor: string;
};

function formatRevenueValue(amount: number): string {
  return formatAnalyticsCurrency(amount, {
    maximumFractionDigits: 2,
    symbol: "€",
    symbolPosition: "suffix",
  });
}

function percentChange(current: number, previous: number): number | null {
  if (current === previous) {
    return 0;
  }

  if (previous === 0) {
    return current === 0 ? 0 : 100;
  }

  return ((current - previous) / previous) * 100;
}

function formatPercentDelta(change: number | null): string | undefined {
  if (change === null) {
    return undefined;
  }

  const sign = change > 0 ? "+" : "";
  const formatted = change.toLocaleString("en-US", {
    maximumFractionDigits: 1,
  });

  return `${sign}${formatted}%${DELTA_SUFFIX()}`;
}

function trendFromChange(change: number | null): MetricCardTrend {
  if (change === null || change === 0) {
    return "neutral";
  }

  return change > 0 ? "positive" : "negative";
}

// DV-119 (owner): hotels see both Earned and Expected, each with a caption.
const earnedDescription = () =>
  at(
    "revenue.partnersEarnedDisclaimer",
    "Updates with a delay, after the accounting report is issued.",
  );
const expectedTitle = () => at("revenue.partnersExpected", "Expected");
const expectedDescription = () =>
  at(
    "revenue.partnersExpectedDisclaimer",
    "An estimate based on Viator data; the final amount may differ.",
  );

export function buildRapidExpectedMetric(
  value: string,
): ServicesRapidRevenueMetric {
  return {
    title: expectedTitle(),
    value,
    description: expectedDescription(),
    icon: createElement(Hourglass),
    iconColor: METRIC_CARD_ICON_COLOR,
  };
}

export function buildRapidTotalRevenueMetric(input: {
  /** Partner orders-summary `earnings`: the hotel's earned commission share. */
  currentEarnings?: number;
  previousEarnings?: number;
  includeDelta: boolean;
}): ServicesRapidRevenueMetric {
  const currentAmount = input.currentEarnings ?? 0;
  const previousAmount = input.previousEarnings ?? 0;
  const change = input.includeDelta
    ? percentChange(currentAmount, previousAmount)
    : null;

  return {
    // Task 0038 sim fix (2026-09-30): "Earned", not "Total Revenue" — paired
    // with the "Expected" tile next to it, consistent with the Earned/
    // Expected wording on the other 4 partner surfaces.
    title: at("revenue.partnersEarned", "Earned"),
    value: formatRevenueValue(currentAmount),
    delta: formatPercentDelta(change),
    description: earnedDescription(),
    trend: trendFromChange(change),
    icon: createElement(Euro),
    iconColor: METRIC_CARD_ICON_COLOR,
  };
}

export const RAPID_TOTAL_REVENUE_LOADING_METRIC: ServicesRapidRevenueMetric = {
  title: at("revenue.partnersEarned", "Earned"),
  value: "0€",
  description: earnedDescription(),
  icon: createElement(Euro),
  iconColor: METRIC_CARD_ICON_COLOR,
};
